import { computed, observable, action, makeObservable } from 'mobx';
import geojsonRbush from 'geojson-rbush';
import { formatBuildingFinancials } from '../lib/formatData';
import ProjectStore from './ProjectStore';
import UserStore from './UserStore';
import axios from '../lib/axios';
import agent from '../lib/agent';
import { getDistanceFromLatLonInKm } from '../lib/utils';

import { POPULAR_RANGE_FILTERS, RANGE_FILTERS } from '../constants/filters';

class Store {
  id = null;
  name = '';
  description = '';
  filters = {};
  acceptedFiles = [];
  lat = '';
  lon = '';
  address = '';
  color = '';
  files = [];
  executiveSummaryFileId = null;
  tasks = [];
  financials = null;
  resultMap = new Map();
  survey = [];
  groupId = null;
  buildingFilter = null;
  members = [];
  buildings = [];
  activeLayers = [];
  layers = [];
  isLoading = false;
  projectBuildingRegistry = new Map();
  bbox = null;
  pickedResults = new Set();
  vendors = [];
  isVendor = false;
  proposals = [];
  termsAccepted = true;
  infoMessage = '';
  seen = 'none';
  terms = null;
  status = 'in_progress';
  vendor = null;
  variant = 'large';
  headerInfo = null;
  headerVariant = 'map';
  infoMessageOpen = false;
  buildingOsmIds = new Set();
  created = null;

  proposalMessage = '';
  contactMessage = '';
  customerName = '';
  proposalDeadline = '';

  rfpQuestions = [];
  technologies = [
    {
      id: 'solar',
      label: 'Solar'
    },
    {
      id: 'chp',
      label: 'Combined Heat and Power'
    },
    {
      id: 'fuelcell',
      label: 'Fuel Cell'
    },
    {
      id: 'storage',
      label: 'Storage'
    },
    {
      id: 'efficiency',
      label: 'Energy Efficiency'
    },
    {
      id: 'other',
      label: 'Other'
    }
  ];

  recommendations = {
    chp: 0,
    solar: 0,
    storage: 0,
    fuel_cell: 0
  };

  loadProject = async projectId => {
    this.isLoading = true;

    const project = await ProjectStore.loadProject(projectId);
    if (project) {
      this.variant = 'large';
      this.headerVariant = 'map';
      this.id = project.id;
      this.financials = null;
      this.name = project.name;
      this.buildingFilter = null;
      this.financials = project.financials;
      this.description = project.description;
      this.color = project.color;
      this.files = project.files;
      this.tasks = project.tasks;
      this.address = project.address;
      this.lat = project.lat;
      this.lon = project.lon;
      this.survey = project.survey;
      this.members = project.members;
      this.layers = project.layers;
      this.created = project.created;
      this.filters = project.filters;

      this.proposalDeadline = project.proposal_deadline_at;
      this.executiveSummaryFileId = project.executive_summary_file_id;
      this.customerName = project.customer_name;
      this.proposalMessage = project.markdown_proposal_message;
      this.contactMessage = project.markdown_proposal_contact;

      this.bbox = project.bbox;
      this.vendors = project.vendors;
      this.groupId = project.group_id;
      this.isVendor = project.is_vendor;
      this.proposals = project.proposals;
      this.termsAccepted = project.terms_accepted;
      this.seen = project.seen;
      this.infoMessage = project.info_message;
      this.status = project.status;
      this.vendor = null;
      this.terms = project.terms;
      this.recommendations = project.recommendations;

      if (this.isVendor) {
        this.setVendor(UserStore.user.group);
      }

      if (!this.termsAccepted) {
        this.isLoading = false;
        return null;
      }

      this.setBuildings(project.buildings);
      this.buildingOsmIds = new Set(
        project.buildings.map(building => building.original_id)
      );

      const fetchBuildings = async () => {
        const response = await agent.Project.getProjectBuildings(this.id);
        const { data, financials } = response.data;
        if (financials) {
          this.setFinancials(financials);
        }
        if (data.every(building => building.financials)) {
          this.setBuildings(data);
        }

        if (!this.financialsCalculated) setTimeout(fetchBuildings, 5000);
      };

      if (!this.financialsCalculated) {
        fetchBuildings();
      }

      this.isLoading = false;
      return project;
    }

    this.isLoading = false;
    return null;
  };

  loadBuildings = async () => {
    const response = await agent.Project.getProjectBuildings(this.id);
    const { data, financials } = response.data;
    this.setBuildings(data);

    if (financials) {
      this.setFinancials(financials);
    }

    if (data.every(building => building.financials)) {
      return data;
    }
    return false;
  };

  setName = name => {
    this.name = name;
    agent.Project.updateProject(this.id, { name });
    const resultMap = new Map(ProjectStore.resultMap);
    const result = resultMap.get(this.id);
    if (result) {
      resultMap.set(this.id, {
        ...result,
        name
      });

      ProjectStore.setResults(resultMap);
    }
  };

  setHeaderVariant = variant => {
    this.headerVariant = variant;
  };

  setHeaderInfo = data => {
    this.headerInfo = data;
  };

  getProjectFilters = () => {
    const { filters } = this;
    const newPopularRangeFilters = JSON.parse(
      JSON.stringify({ ...POPULAR_RANGE_FILTERS })
    );

    const newRangeFilters = JSON.parse(JSON.stringify({ ...RANGE_FILTERS }));

    Object.entries(filters).forEach(([key, value]) => {
      Object.entries(newRangeFilters).forEach(([title, filters]) => {
        const idx = filters.findIndex(f => f.name === key);

        if (idx !== -1) {
          if (value.min === null || value.max === null) {
            newRangeFilters[title][idx].isVisible = false;
          } else {
            newRangeFilters[title][idx] = {
              ...newRangeFilters[title][idx],
              start: value.min,
              end: value.max
            };
          }
        }
      });
    });

    // remove empty categories
    Object.entries(newRangeFilters).forEach(([key, value]) => {
      if (value.every(f => f.isVisible === false)) {
        delete newRangeFilters[key];
      }
    });

    Object.entries(filters).forEach(([key, value]) => {
      const idx = newPopularRangeFilters.popular.findIndex(f => f.name === key);
      if (idx !== -1) {
        newPopularRangeFilters.popular[idx] = {
          ...newPopularRangeFilters.popular[idx],
          start: value.min,
          end: value.max
        };
      }
    });

    return {
      popular: newPopularRangeFilters,
      rangeFilters: newRangeFilters
    };
  };

  setFilters = projectFilters => {
    this.filters = projectFilters;
  };

  setBuildings = buildings => {
    this.buildings = buildings.map(building => {
      return {
        ...building,
        // financials: null,
        formatted_financials: building.financials
          ? formatBuildingFinancials(building.financials)
          : null
      };
    });
  };

  loadResult = async ids => {
    if (!ids.length) {
      return;
    }

    // Set state to loading
    ids.forEach(id => {
      this.resultMap.set(id, 1);
    });

    const response = await agent.Project.getProjectBuildings(this.id);

    response.data.data.forEach(building => {
      this.resultMap.set(building.id, {
        ...building,
        formatted_financials: building.financials
          ? formatBuildingFinancials(building.financials)
          : null
      });
    });

    return response.data.data;
  };

  setDescription = description => {
    this.description = description;
  };

  addFile = file => {
    const newFiles = [...this.files, file];
    this.files = newFiles;
  };

  constructor() {
    makeObservable(this, {
      id: observable,
      name: observable,
      description: observable,
      resultMap: observable,
      financials: observable,
      lat: observable,
      lon: observable,
      address: observable,
      rfpQuestions: observable,
      color: observable,
      files: observable,
      layers: observable,
      tasks: observable,
      survey: observable,
      groupId: observable,
      members: observable,
      buildings: observable,
      isLoading: observable,
      acceptedFiles: observable,
      infoMessageOpen: observable,

      proposalMessage: observable,
      executiveSummaryFileId: observable,
      contactMessage: observable,
      customerName: observable,
      proposalDeadline: observable,

      projectBuildingRegistry: observable,
      bbox: observable,
      vendors: observable,
      isVendor: observable,
      proposals: observable,
      termsAccepted: observable,
      buildingFilter: observable,
      filters: observable,
      terms: observable,
      infoMessage: observable,
      seen: observable,
      status: observable,
      vendor: observable,
      variant: observable,
      headerInfo: observable,
      headerVariant: observable,
      recommendations: observable,
      activeLayers: observable,
      loadProject: action,
      setTermsAccepted: action,
      setInfoMessageOpen: action,
      setName: action,
      setHeaderVariant: action,
      setHeaderInfo: action,
      setBuildings: action,
      setDescription: action,
      addFile: action,
      financialsCalculated: computed,
      filteredBuildings: computed,
      setFileId: action,
      deleteFiles: action,
      setFilters: action,
      removeBuilding: action,
      addFiles: action,
      addProposals: action,
      setMembers: action,
      setVendors: action,
      selectedVendors: computed,
      addBuilding: action,
      submit: action,
      acceptTerms: action,
      acceptInfoMessage: action,
      setInfoMessage: action,
      setTerms: action,
      setStatus: action,
      setVendor: action,
      setVariant: action,
      updateBuildingName: action
    });
  }

  get financialsCalculated() {
    return this.buildings.every(building => building.financials);
  }

  get filteredBuildings() {
    const geometry = this.buildingFilter?.geometry;
    if (!geometry) return this.buildings;

    const tree = geojsonRbush();
    tree.insert({
      type: 'Feature',
      geometry
    });

    if (geometry.type === 'Polygon') {
      return this.buildings.filter(building => {
        return tree.collides({
          type: 'Feature',
          geometry: building.polygon
        });
      });
    }

    if (geometry.type === 'Point') {
      return this.buildings.reduce((acc, building) => {
        const distance = getDistanceFromLatLonInKm(
          geometry.coordinates,
          building.centroid
        );

        if (distance < 0.5) {
          acc.push({
            ...building,
            distance
          });
        }

        return acc;
      }, []);
    }

    return this.buildings;
  }

  setFileId = (fileTempId, fileId) => {
    const index = this.files.findIndex(file => file.id === fileTempId);
    if (index > -1) {
      this.files[index].id = fileId;
    }
  };

  setTerms = terms => {
    this.terms = terms;
  };

  deleteFiles = async fileIds => {
    this.files = [...this.files].filter(file => !fileIds.includes(file.id));
    await agent.Workspace.delete(fileIds);
  };

  removeBuilding = async buildingId => {
    this.buildings = [...this.buildings].filter(
      building => building.id !== buildingId
    );
  };

  addFiles = async (fileIds, buildingId) => {
    await agent.Project.addFiles(this.id, fileIds, buildingId);
  };

  addProposals = async fileIds => {
    await agent.Project.addProposals(this.id, fileIds);
  };

  setMembers = async members => {
    this.members = members;
    await agent.Project.addMembers(this.id, {
      data: members.map(({ id }) => id)
    });
  };

  setVendors = async vendors => {
    this.vendors = vendors;
  };

  getVendors = async () => {
    const response = await agent.Project.getProjectVendors(this.id);
    this.setVendors(response.data.data);
  };

  removeVendor = async groupId => {
    await agent.Project.removeVendor(this.id, groupId);
    const newVendors = this.vendors.filter(vendor => vendor.id !== groupId);
    this.setVendors(newVendors);
  };

  addVendor = async groupIdLike => {
    const { data } = await agent.Project.addVendor(this.id, groupIdLike);
    const newVendors = [...this.vendors, data];
    this.setVendors(newVendors);
  };

  inviteVendorByEmail = async email => {
    await agent.Project.inviteVendor(this.id, email);
  };

  get selectedVendors() {
    return this.vendors.filter(vendor => vendor.is_selected);
  }

  addBuilding(building) {
    this.buildings = [...this.buildings, building];
  }

  submit = () => {
    const data = {
      title: this.title,
      description: this.description,
      users: this.users,
      files: this.files,
      project_id: this.project_id
    };

    if (this.id) {
      data.id = this.id;
    }
  };

  acceptTerms = async (projectId, terms) => {
    const data = JSON.parse(JSON.stringify(terms || this.terms));
    const response = await axios.post(
      `/project/${projectId || this.id}/accept`,
      data
    );
    if (response.data.err?.code === 'terms_updated') {
      this.setTerms(response.data.data);
      return false;
    }
    this.termsAccepted = true;
    return true;
  };

  setTermsAccepted = termsAccepted => {
    this.termsAccepted = termsAccepted;
  };

  acceptInfoMessage = async () => {
    agent.Project.acceptInfoMessage(this.id);
  };

  setInfoMessage(message) {
    this.infoMessage = message;
    agent.Project.setProjectSettings(this.id, {
      info_message: message
    });
  }

  setProposalMessage(message) {
    this.proposalMessage = message;
  }

  setContactMessage(message) {
    this.contactMessage = message;
  }

  setExecutiveSummaryFileId(fileId) {
    this.executiveSummaryFileId = fileId;
  }

  setCustomerName(customerName) {
    this.customerName = customerName;
  }

  setProposalDeadline(deadline) {
    this.proposalDeadline = deadline;
  }

  setStatus(status, options = {}) {
    this.status = status;
    const resultMap = new Map(ProjectStore.resultMap);
    const project = resultMap.get(this.id);
    if (project) {
      project.status = status;
    }

    agent.Project.setProjectSettings(this.id, {
      status,
      ...options
    });

    ProjectStore.setResults(resultMap);
  }

  setVendor(vendor) {
    this.vendor = vendor;
  }

  setVariant(variant) {
    this.variant = variant;
  }

  setRFPQuestions(questions) {
    this.rfpQuestions = questions;
  }

  getRFPQuestion(questionId) {
    return this.rfpQuestions.find(q => q.question_id === questionId);
  }

  setRFPQuestionActive = (questionId, isActive) => {
    this.rfpQuestions = this.rfpQuestions.map(q => {
      if (q.question_id === questionId)
        return {
          ...q,
          is_active: isActive
        };
      return q;
    });

    agent.Project.updateProjectRFPQuestion(this.id, questionId, {
      is_active: isActive
    });
  };

  loadRFPQuestions = async () => {
    const { data } = await agent.Project.getProjectRFPQuestions(this.id);
    this.setRFPQuestions(data.data);
  };

  deleteRFPQuestion = async questionId => {
    await agent.Project.deleteProjectRFPQuestion(this.id, questionId);
  };

  setProposals(proposals) {
    this.proposals = proposals;
  }

  setFinancials(financials) {
    this.financials = financials;
  }

  // eslint-disable-next-line class-methods-use-this
  get analyticsUrl() {
    return null;
  }

  getFinancials = async projectId => {
    const { data } = await agent.Project.getProjectFinancials(
      projectId || this.id,
      this.id
    );

    this.financials = data.data;
  };

  get emissionsData() {
    console.log('this.financials', this.financials);
    if (this.financials) {
      return this.financials.emissions;
    }
    return null;
  }

  get cashFlow() {
    if (this.financials?.capex_cash_flow) {
      return this.financials.capex_cash_flow;
    }
    return null;
  }

  get formattedFinancials() {
    return this.financials.formatted_financials;
  }

  getLayers = async () => {
    const response = await agent.Project.getProjectLayers(this.id);
    this.layers = response.data.data;
  };

  setLayerActive(layerId, isActive) {
    const layers = [...this.activeLayers, layerId];
    if (!isActive) {
      this.setActiveLayers([
        ...this.activeLayers.filter(activeLayerId => activeLayerId !== layerId)
      ]);
    } else {
      this.setActiveLayers(layers);
    }
  }

  setActiveLayers(activeLayers) {
    this.activeLayers = [...new Set(activeLayers)];
  }

  loadProposals = async () => {
    const response = await agent.Project.getProposals(this.id);
    this.setProposals(response.data.data);
  };

  deleteProposal = async proposalId => {
    await agent.Project.deleteProposal(this.id, proposalId);
  };

  setBuildingFilter(filter) {
    this.buildingFilter = filter;
  }

  setAcceptedFiles(files) {
    this.acceptedFiles = files;
  }

  waitProjectReady = async (
    onProgress = () => {
      return 0;
    },
    timeout = 10 * 60 * 1000
  ) => {
    return new Promise(resolve => {
      const interval = setInterval(async () => {
        const status = await this.getStatus();
        const totalProgress =
          status.progress + 10 <= 100 ? status.progress + 10 : status.progress;

        onProgress(totalProgress);
        if (status.progress === 100) {
          const buildings = await this.loadBuildings();
          if (buildings) {
            clearInterval(interval);
            resolve();
          }
        }
      }, 5000);

      setTimeout(() => {
        clearInterval(interval);
        resolve();
      }, timeout);
    });
  };

  getStatus = async () => {
    const response = await agent.Project.getStatus(this.id);
    return response.data;
  };

  getProjectBuildingByOsmId = osmIdLike => {
    if (!osmIdLike) return null;
    return this.buildings.find(building => {
      const osmId = osmIdLike.replace('osm-', '').toUpperCase();
      console.log('INCLUDES', building.original_id, osmId);
      return building.original_id.includes(osmId);
    });
  };

  hasBuildingWithOsmId = osmIdLike => {
    if (!osmIdLike) return false;
    const osmId = osmIdLike.replace('osm-', '').toUpperCase();
    return this.buildingOsmIds.has(osmId);
  };

  updateBuildingName(buildingId, buildingName) {
    const projectResult = ProjectStore.resultMap.get(this.id);
    const newProjectResult = { ...projectResult };
    const newResults = new Map(ProjectStore.resultMap);
    newProjectResult.buildings = newProjectResult.buildings.map(building => {
      return {
        ...building,
        name: building.id === buildingId ? buildingName : building.name
      };
    });

    newResults.set(this.id, newProjectResult);
    ProjectStore.setResults(newResults);

    this.buildings = [...this.buildings].map(building => {
      if (building.id === buildingId) {
        return {
          ...building,
          name: buildingName
        };
      }

      return building;
    });
  }

  setInfoMessageOpen = open => {
    this.infoMessageOpen = open;
  };
}

const store = new Store();
export default store;
